import { useEffect } from "react"
import { Head, Link, router, usePoll } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { StarButton } from "@/components/StarButton"
import { PageHeader } from "@/components/PageHeader"
import { PlanSetsSection } from "@/components/plan-sets/PlanSetsSection"
import { TakeoffsSection } from "@/components/projects/TakeoffsSection"
import { anyProcessing, type PlanSetRow } from "@/lib/plan-set-status"
import type { TakeoffListRow } from "@/types/takeoff"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable, DataRow } from "@/components/ui/data-table"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import { PROJECT_STATUSES, STATUS_LABELS, STATUS_TONES, type ProjectStatus } from "@/lib/project-status"

type ProjectDetail = {
  id: number
  lot_number: string | null
  plan_name: string | null
  elevation: string | null
  address: string | null
  notes: string | null
  status: ProjectStatus
  archived: boolean
  community: string | null
  community_id: number | null
  starred: boolean
  customer: string | null
  display_name: string
  created_by: string
  created_at: string
  updated_at: string
}

export default function ProjectsShow({
  project,
  plan_sets: planSets,
  takeoffs,
}: {
  project: ProjectDetail
  plan_sets: PlanSetRow[]
  takeoffs: TakeoffListRow[]
}) {
  const changeStatus = (status: string) => {
    router.patch(`/projects/${project.id}`, { status }, { preserveScroll: true })
  }

  // Refresh plan-set/sheet processing status while any background work is running.
  const active = anyProcessing(planSets)
  const { start, stop } = usePoll(2000, { only: ["plan_sets"] }, { autoStart: false })
  useEffect(() => {
    if (active) start()
    else stop()
  }, [active, start, stop])

  const description = project.customer
    ? project.community
      ? `${project.customer} · ${project.community}`
      : project.customer
    : "One-off project"

  return (
    <>
      <Head title={project.display_name}>
        <meta name="description" content={`Project details for ${project.display_name} (${description}).`} />
        <meta property="og:title" content={project.display_name} />
        <meta property="og:description" content={`Project details for ${project.display_name} (${description}).`} />
      </Head>
      <AppShell>
        <PageHeader
          title={
            <span className="flex items-center gap-3">
              {project.display_name}
              {project.archived && <Badge tone="muted">Archived</Badge>}
            </span>
          }
          description={description}
          actions={
            <>
              <StarButton type="Project" id={project.id} starred={project.starred} />
              <Select
                aria-label="Project status"
                value={project.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="w-36"
              >
                {PROJECT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
              <Button asChild variant="secondary">
                <Link href={`/projects/${project.id}/edit`}>Edit</Link>
              </Button>
              {project.archived ? (
                <Button
                  variant="secondary"
                  onClick={() => router.patch(`/projects/${project.id}/unarchive`)}
                >
                  Unarchive
                </Button>
              ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost">Archive</Button>
                  </DialogTrigger>
                  <DialogContent size="sm">
                    <DialogHeader>
                      <DialogTitle>Archive this project?</DialogTitle>
                      <DialogDescription>
                        Archived projects disappear from the default list but stay
                        searchable with “Show archived” — nothing is deleted.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="ghost">Cancel</Button>
                      </DialogClose>
                      <Button
                        variant="danger"
                        onClick={() => router.patch(`/projects/${project.id}/archive`)}
                      >
                        Archive project
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </>
          }
        />

        <div className="mt-6">
          <DataTable>
            <DataRow title="Status">
              <Badge tone={STATUS_TONES[project.status]}>
                {STATUS_LABELS[project.status]}
              </Badge>
            </DataRow>
            <DataRow title="Customer / Community">
              {project.community_id ? (
                <Link href={`/communities/${project.community_id}`}>{description}</Link>
              ) : (
                description
              )}
            </DataRow>
            <DataRow title="Lot #">{project.lot_number || "—"}</DataRow>
            <DataRow title="Plan">{project.plan_name || "—"}</DataRow>
            <DataRow title="Elevation">{project.elevation || "—"}</DataRow>
            <DataRow title="Address">{project.address || "—"}</DataRow>
            <DataRow title="Created">
              {formatDateTime(project.created_at)} by {project.created_by}
            </DataRow>
            <DataRow title="Updated">{formatDateTime(project.updated_at)}</DataRow>
            <DataRow title="Notes">
              {project.notes ? (
                <span className="whitespace-pre-wrap">{project.notes}</span>
              ) : (
                "—"
              )}
            </DataRow>
          </DataTable>
        </div>

        <PlanSetsSection projectId={project.id} planSets={planSets} />
        <TakeoffsSection projectId={project.id} takeoffs={takeoffs} />
      </AppShell>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
